/**
 * sessionStore - local identity for this tab: display name, meeting role,
 * and room. This is the only live identity source. Persistence stays at the
 * entry and multiplayer owners because the store owns application state, not
 * durable preferences.
 *
 * localStorage persistence stays at the call sites (loginFlow persists all
 * three; multiplayer persists role/name on change) - this store owns the
 * live value, not the durable one.
 *
 * `role` is a plain string, not a union: roles.json is the runtime source of
 * truth (open access can introduce keys), and every capability check goes
 * through can(role, capability). '' means "not resolved yet" (pre-login).
 */
import { createAppStore } from './createAppStore';

export interface SessionState {
  playerName: string;
  userRole: string;
  roomName: string;
}

function initialSessionState(): SessionState {
  return { playerName: '', userRole: '', roomName: '' };
}

export const sessionStore = createAppStore<SessionState>(initialSessionState());

/**
 * Patch resolved identity fields synchronously.
 */
export function setIdentity(patch: Partial<SessionState>): void {
  const current = sessionStore.getState();
  const next: Partial<SessionState> = {};
  for (const key of Object.keys(current) as Array<keyof SessionState>) {
    const value = patch[key];
    if (typeof value !== 'string' || value === current[key]) continue;
    next[key] = value;
  }
  if (Object.keys(next).length) sessionStore.setState(next);
}

export function getIdentity(): SessionState {
  return sessionStore.getState();
}

export function resetSession(): void {
  sessionStore.setState(initialSessionState(), true);
}
